import { DataAction, EmptyAction, MessageAction } from 'src/CustomTypes'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { AnyAction } from 'redux'
import { RootState } from 'src/Store'
import { MapLegend, MapLegendObject } from 'src/dataSources/geo/MapLegend'
import { DataTheme, ToolType } from 'src/Enums'
import { GeoModel, GeoModelService } from 'src/dataSources/geo/GeoModel'
import { Address, AddressResult } from 'src/dataSources/dataforsyningen/Address'
import { Graph } from 'src/dataSources/graphs/GraphRegistry'

export const ADD_GRAPH_GROUP = 'GENERAL_ACTIONS_ADD_GRAPH_GROUP'
export const FETCHING_ADDRESSES_STARTED = 'GENERAL_ACTIONS_FETCHING_ADDRESSES_STARTED'
export const FETCHING_ADDRESSES_SUCCESS = 'GENERAL_ACTIONS_FETCHING_ADDRESSES_SUCCESS'
export const FETCHING_ADDRESSES_FAILURE = 'GENERAL_ACTIONS_FETCHING_ADDRESSES_FAILURE'
export const FETCHING_GEO_MODELS_STARTED = 'GENERAL_ACTIONS_FETCHING_GEO_MODELS_STARTED'
export const FETCHING_GEO_MODELS_SUCCESS = 'GENERAL_ACTIONS_FETCHING_GEO_MODELS_SUCCESS'
export const FETCHING_GEO_MODELS_FAILURE = 'GENERAL_ACTIONS_FETCHING_GEO_MODELS_FAILURE'
export const FETCHING_MAP_LEGEND_STARTED = 'GENERAL_ACTIONS_FETCHING_MAP_LEGEND_STARTED'
export const FETCHING_MAP_LEGEND_SUCCESS = 'GENERAL_ACTIONS_FETCHING_MAP_LEGEND_SUCCESS'
export const FETCHING_MAP_LEGEND_FAILURE = 'GENERAL_ACTIONS_FETCHING_MAP_LEGEND_FAILURE'
export const REMOVE_GRAPH_GROUP = 'GENERAL_ACTIONS_REMOVE_GRAPH_GROUP'
export const SET_ADDRESS_QUERY = 'GENERAL_ACTIONS_SET_ADDRESS_QUERY'
export const SET_ADDRESSES = 'GENERAL_ACTIONS_SET_ADDRESSES'
export const SET_BOTTOM_BAR_DRAGGING = 'GENERAL_ACTIONS_SET_BOTTOM_BAR_DRAGGING'
export const SET_BOTTOM_BAR_HEIGHT = 'GENERAL_ACTIONS_SET_BOTTOM_BAR_HEIGHT'
export const SET_BOTTOM_BAR_WIDTH = 'GENERAL_ACTIONS_SET_BOTTOM_BAR_WIDTH'
export const SET_DATA_THEME = 'GENERAL_ACTIONS_SET_DATA_THEME'
export const SET_RIGHT_SIDE_BAR_DRAGGING = 'GENERAL_ACTIONS_SET_RIGHT_SIDE_BAR_DRAGGING'
export const SET_RIGHT_SIDE_BAR_WIDTH = 'GENERAL_ACTIONS_SET_RIGHT_SIDE_BAR_WIDTH'
export const SET_SELECTED_GEO_MODEL = 'GENERAL_ACTIONS_SET_SELECTED_GEO_MODEL'
export const SET_SELECTED_GRAPH = 'GENERAL_ACTIONS_SET_SELECTED_GRAPH'
export const SET_SELECTED_TOOL = 'GENERAL_ACTIONS_SET_SELECTED_TOOL'
export const SET_SHOW_CO2LEVELSORG_GRAPHS = 'GENERAL_ACTIONS_SET_SHOW_CO2LEVELSORG_GRAPHS'
export const SET_SHOW_LEFT_SIDE_BAR = 'GENERAL_ACTIONS_SET_SHOW_LEFT_SIDE_BAR'

interface GeneralActions {
  addGraphGroup: (graphGroup: string) => DataAction<string>
  fetchingAddressesStarted: () => EmptyAction
  fetchingAddressesFailure: (message: string) => MessageAction
  fetchingAddressesSuccess: (addresses: AddressResult[]) => DataAction<AddressResult[]>
  fetchingGeoModelsStarted: () => EmptyAction
  fetchingGeoModelsFailure: (message: string) => MessageAction
  fetchingGeoModelsSuccess: (geoModels: GeoModel[] | null) => DataAction<GeoModel[] | null>
  fetchingMapLegendStarted: () => EmptyAction
  fetchingMapLegendFailure: (message: string) => MessageAction
  fetchingMapLegendSuccess: (mapLegend: MapLegendObject[] | null) => DataAction<MapLegendObject[] | null>
  removeGraphGroup: (graph: string) => DataAction<string>
  setAddressQuery: (query: string) => DataAction<string>
  setBottomBarHeight: (height: number) => DataAction<number>
  setBottomBarWidth: (width: number) => DataAction<number>
  setBottomBarDragging: (dragging: Boolean) => DataAction<Boolean>
  setDataTheme: (dataTheme: DataTheme) => DataAction<DataTheme>
  setRightSideBarDragging: (dragging: Boolean) => DataAction<Boolean>
  setRightSideBarWidth: (width: number) => DataAction<number>
  setSelectedGeoModel: (model: GeoModel | undefined) => DataAction<GeoModel | undefined>
  setSelectedGraph: (graph: Graph | undefined) => DataAction<Graph | undefined>
  setSelectedTool: (toolType: ToolType) => DataAction<ToolType>
  setShowLeftSideBar: (show: boolean) => DataAction<boolean>

  // Thunk actions.
  fetchAddresses: (query: string) => ThunkAction<Promise<void>, RootState, {}, AnyAction>
  fetchGeoModels: (x: number | undefined, y: number | undefined) => ThunkAction<Promise<void>, RootState, {}, AnyAction>
  fetchMapLegend: (modelId: string) => ThunkAction<Promise<void>, RootState, {}, AnyAction>
}

export const generalActions: GeneralActions = {
  addGraphGroup: (graphGroup: string): DataAction<string> => {
    return {
      type: ADD_GRAPH_GROUP,
      data: graphGroup
    }
  },
  fetchingAddressesStarted: (): EmptyAction => {
    return {
      type: FETCHING_ADDRESSES_STARTED
    }
  },
  fetchingAddressesFailure: (message: string): MessageAction => {
    return {
      type: FETCHING_ADDRESSES_FAILURE,
      message: message
    }
  },
  fetchingAddressesSuccess: (addresses: AddressResult[]): DataAction<AddressResult[]> => {
    return {
      type: FETCHING_ADDRESSES_SUCCESS,
      data: addresses
    }
  },
  fetchingGeoModelsStarted: (): EmptyAction => {
    return {
      type: FETCHING_GEO_MODELS_STARTED
    }
  },
  fetchingGeoModelsFailure: (message: string): MessageAction => {
    return {
      type: FETCHING_GEO_MODELS_FAILURE,
      message: message
    }
  },
  fetchingGeoModelsSuccess: (models: GeoModel[] | null): DataAction<GeoModel[] | null> => {
    return {
      type: FETCHING_GEO_MODELS_SUCCESS,
      data: models
    }
  },
  fetchAddresses: (query: string): ThunkAction<Promise<void>, RootState, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<RootState, {}, AnyAction>, getState: () => RootState): Promise<void> => {
      // Fetch addresses
      dispatch(generalActions.fetchingAddressesStarted())
      const wsAddress = new Address()
      wsAddress.setQuery(query)
      let addresses: AddressResult[]
      try {
        addresses = await wsAddress.fetchAddresses()
        dispatch(generalActions.fetchingAddressesSuccess(addresses))
      } catch (err) {
        console.error(err)
        const errorMessage: string = (err as Error).message
        dispatch(generalActions.fetchingAddressesFailure(errorMessage))
      }
    }
  },
  fetchGeoModels: (x: number | undefined, y: number | undefined): ThunkAction<Promise<void>, RootState, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<RootState, {}, AnyAction>, getState: () => RootState): Promise<void> => {
      // Fetch models.
      const token = getState().authState.token
      dispatch(generalActions.fetchingGeoModelsStarted())
      const wsGeoModels = new GeoModelService(token)
      if (x !== undefined && y !== undefined) {
        wsGeoModels.setX(x)
        wsGeoModels.setY(y)
      }
      let models: GeoModel[]
      try {
        models = await wsGeoModels.fetchModels()
        dispatch(generalActions.fetchingGeoModelsSuccess(models))
      } catch (err) {
        console.error(err)
        const errorMessage: string = (err as Error).message
        dispatch(generalActions.fetchingGeoModelsFailure(errorMessage))
      }
    }
  },
  fetchingMapLegendStarted: (): EmptyAction => {
    return {
      type: FETCHING_MAP_LEGEND_STARTED
    }
  },
  fetchingMapLegendFailure: (message: string): MessageAction => {
    return {
      type: FETCHING_MAP_LEGEND_FAILURE,
      message: message
    }
  },
  fetchingMapLegendSuccess: (borehole: MapLegendObject[] | null): DataAction<MapLegendObject[] | null> => {
    return {
      type: FETCHING_MAP_LEGEND_SUCCESS,
      data: borehole
    }
  },
  fetchMapLegend: (modelId: string): ThunkAction<Promise<void>, RootState, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<RootState, {}, AnyAction>, getState: () => RootState): Promise<void> => {
      // Fetch map legend.
      const token = getState().authState.token
      dispatch(generalActions.fetchingMapLegendStarted())
      const wsMapLegend = new MapLegend(token)
      wsMapLegend.setModelId(modelId)
      let mapLegend: MapLegendObject[]
      try {
        mapLegend = await wsMapLegend.fetchMapLegend()
        dispatch(generalActions.fetchingMapLegendSuccess(mapLegend))
      } catch (err) {
        console.error(err)
        const errorMessage: string = (err as Error).message
        dispatch(generalActions.fetchingMapLegendFailure(errorMessage))
      }
    }
  },
  removeGraphGroup: (graphGroup: string): DataAction<string> => {
    return {
      type: REMOVE_GRAPH_GROUP,
      data: graphGroup
    }
  },
  setAddressQuery: (query: string): DataAction<string> => {
    return {
      type: SET_ADDRESS_QUERY,
      data: query
    }
  },
  setBottomBarHeight: (height: number): DataAction<number> => {
    return {
      type: SET_BOTTOM_BAR_HEIGHT,
      data: height
    }
  },
  setBottomBarWidth: (width: number): DataAction<number> => {
    return {
      type: SET_BOTTOM_BAR_WIDTH,
      data: width
    }
  },
  setBottomBarDragging: (dragging: Boolean): DataAction<Boolean> => {
    return {
      type: SET_BOTTOM_BAR_DRAGGING,
      data: dragging
    }
  },
  setDataTheme: (dataTheme: DataTheme): DataAction<DataTheme> => {
    return {
      type: SET_DATA_THEME,
      data: dataTheme
    }
  },
  setRightSideBarDragging: (dragging: Boolean): DataAction<Boolean> => {
    return {
      type: SET_RIGHT_SIDE_BAR_DRAGGING,
      data: dragging
    }
  },
  setRightSideBarWidth: (width: number): DataAction<number> => {
    return {
      type: SET_RIGHT_SIDE_BAR_WIDTH,
      data: width
    }
  },
  setSelectedGeoModel: (model: GeoModel | undefined): DataAction<GeoModel | undefined> => {
    return {
      type: SET_SELECTED_GEO_MODEL,
      data: model
    }
  },
  setSelectedGraph: (graph: Graph | undefined): DataAction<Graph | undefined> => {
    return {
      type: SET_SELECTED_GRAPH,
      data: graph
    }
  },
  setSelectedTool: (toolType: ToolType): DataAction<ToolType> => {
    return {
      type: SET_SELECTED_TOOL,
      data: toolType
    }
  },
  setShowLeftSideBar: (show: boolean): DataAction<boolean> => {
    return {
      type: SET_SHOW_LEFT_SIDE_BAR,
      data: show
    }
  }
}
